'use client';

import React, { useMemo, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import {
  faUpRightAndDownLeftFromCenter,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { InfoRow } from '../InfoRow';
import { ProfileDrawerActions } from '../ProfileDrawerActions';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useSite } from 'src/context/providers/SiteProvider';
import { ProfileDrawerCRMLinks } from '../ProfileDrawerCRMLinks';
import { RetiredChip } from 'src/components/PulseUI';
import { format as formatDate } from 'date-fns';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { LOCATION } from 'src/hooks/filters/constants';
import { useGetProviderStrategyTabsQuery } from 'src/api/redux/strategy/chainedStrategiesApi';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { IconButton } from 'src/components/PulseUI/IconButton';
import { getProfileUrl } from 'src/utils/profileUrl';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import StrategyChainsCell from 'src/components/Table/StrategyChainsCell';

interface ProfileDrawerHeaderProps {
  onClose?: () => void;
}

const ProfileDrawerHeader: React.FC<ProfileDrawerHeaderProps> = ({
  onClose,
}) => {
  const router = useRouter();
  const pathName = usePathname();
  const { dispatch } = useSite();
  const { profileStrategies } = useFlags();

  const { entityDetails } = useProfile();
  const { selectedChainIds } = useStrategies(LOCATION.STRATEGY_CENTER);
  const isFromStrategyCenter = pathName?.includes('strategy-center');

  // Fetch strategy chains for this provider (only when from strategy center)
  const { data: strategyTabsResponse } = useGetProviderStrategyTabsQuery(
    { providerId: entityDetails?.id || '' },
    { skip: !isFromStrategyCenter || !entityDetails?.id }
  );

  // Find first selectedChainId matching a provider chain
  const profileChainId = useMemo(() => {
    if (!selectedChainIds.length || !strategyTabsResponse?.results) return null;

    for (const chainId of selectedChainIds) {
      const match = strategyTabsResponse.results.find(
        (chain: any) => chain.id === chainId
      );
      if (match) return match.id;
    }

    return null;
  }, [selectedChainIds, strategyTabsResponse]);

  // Derive chain IDs and metadata for strategy pills
  const { providerChainIds, chainsMetadata } = useMemo(() => {
    const chains = strategyTabsResponse?.results || [];
    return {
      providerChainIds: chains.map((c: any) => c.id),
      chainsMetadata: chains.reduce(
        (acc: Record<number, { label: string }>, c: any) => {
          acc[c.id] = { label: c.label || c.name || `Strategy ${c.id}` };
          return acc;
        },
        {}
      ),
    };
  }, [strategyTabsResponse]);

  const [showMore, setShowMore] = useState(false);

  const headerContent = useMemo(() => {
    if (!entityDetails) return [];

    return [
      { title: 'NPI', value: entityDetails?.id },
      { title: 'Phone', value: entityDetails?.phone },
      { title: 'Email', value: entityDetails?.email },
    ];
  }, [entityDetails]);

  // Extract page context for data-testid (abbreviated)
  const pageContext = useMemo(() => {
    if (!pathName) return 'unk';
    if (pathName.includes('strategy-center')) return 'strat';
    if (pathName.includes('discovery')) return 'disc';
    if (pathName.includes('territories')) return 'terr';
    if (pathName.includes('lists')) return 'list';
    if (pathName.includes('profiles')) return 'prof';
    return 'unk';
  }, [pathName]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      dispatch({ type: 'CLOSE_PROFILE_DRAWER' });
    }
  };
  const handleProfileClick = () => {
    handleClose();

    const { providerId: profileId, profileType } = getEntityIdentifiers(entityDetails);

    // If coming from strategy center with a selected strategy AND profileStrategies flag is enabled, navigate to strategy route
    if (
      profileStrategies &&
      isFromStrategyCenter &&
      profileChainId
    ) {
      // Use chain ID if found
      router.push(
        getProfileUrl(profileType, profileId, `strategy/${profileChainId}`)
      );
      return;
    }

    // Otherwise navigate to standard profile
    router.push(getProfileUrl(profileType, profileId));
  };

  const moreDetails = headerContent && headerContent?.length > 0;
  const isRetired = entityDetails?.retired;
  const deactivatedDate = entityDetails?.deactivation_date
    ? formatDate(new Date(entityDetails?.deactivation_date), 'MM/dd/yyyy')
    : null;

  const cityStateDisplay =
    entityDetails?.primary_address?.city &&
    entityDetails?.primary_address?.state
      ? `${entityDetails?.primary_address?.city}, ${entityDetails?.primary_address?.state}`
      : null;

  return (
    <div className="flex flex-col items-start self-stretch bg-sky-100">
      <div className="flex flex-col items-start self-stretch p-6 gap-4">
        {!entityDetails ? (
          <div className="h-20" />
        ) : (
          <>
            <div className="flex items-start justify-between w-full">
              <div className="flex flex-col">
                <h2
                  className="text-primary font-bold text-2xl leading-tight"
                  data-testid="profile-drawer-title"
                >
                  {entityDetails?.name}
                </h2>
                {isRetired && <RetiredChip deactivatedDate={deactivatedDate} />}
                <div>
                  <div className="text-secondary text-sm">
                    {entityDetails?.specialty}
                  </div>
                  <div className="text-secondary text-sm">
                    {cityStateDisplay}
                  </div>
                </div>
                {isFromStrategyCenter && providerChainIds.length > 0 && (
                  <StrategyChainsCell
                    chainIds={providerChainIds}
                    chainsMetadata={chainsMetadata}
                  />
                )}
              </div>
              <div className="flex items-center ">
                <IconButton
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={handleProfileClick}
                  icon={faUpRightAndDownLeftFromCenter}
                  className="text-primary hover:bg-sky-200"
                  aria-label="Open full profile"
                  data-testid="profile-drawer-expand-button"
                />
                <IconButton
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={handleClose}
                  icon={faXmark}
                  className="text-primary hover:bg-sky-200"
                  aria-label="Close profile drawer"
                  data-testid="profile-drawer-close-button"
                />
              </div>
            </div>

            <div className="flex flex-col items-start gap-2 self-stretch">
              {moreDetails && (
                <>
                  {showMore && (
                    <>
                      {headerContent.map((item) => (
                        <InfoRow key={item.title} item={item} />
                      ))}
                      {entityDetails.crm_links && <ProfileDrawerCRMLinks />}
                    </>
                  )}
                  <button
                    type="button"
                    className="text-link font-medium text-sm cursor-pointer bg-transparent border-none p-0 text-left"
                    onClick={() => setShowMore(!showMore)}
                    aria-expanded={showMore}
                    aria-label={
                      showMore
                        ? 'Show less profile details'
                        : 'Show more profile details'
                    }
                    data-testid={`profile-drawer-toggle-${
                      showMore ? 'less' : 'more'
                    }-${pageContext}-${entityDetails?.id || 'unk'}`}
                  >
                    {showMore ? 'Show Less' : 'Show More'}...
                  </button>
                </>
              )}
            </div>
          </>
        )}
        <ProfileDrawerActions provider={entityDetails} />
      </div>
    </div>
  );
};

export default ProfileDrawerHeader;
